import { type ReactElement, useState } from 'react';
import { X, Plus } from 'lucide-react';
import type { Genre } from '@/lib/types';

type AllGenre = { genreId: string; prettyName: string; canonicalName: string };

type Props = {
  genres: Genre[];
  allGenres: AllGenre[];
  onAdd: (genreId: string) => void;
  onRemove: (genreId: string) => void;
};

export function GenreBadges({ genres, allGenres, onAdd, onRemove }: Props): ReactElement {
  const [pickerOpen, setPickerOpen] = useState(false);

  const assignedGenreIds = new Set(genres.map((g) => g.genreId));
  const unassignedGenres = allGenres.filter((g) => !assignedGenreIds.has(g.genreId));

  function handleSelect(genreId: string): void {
    onAdd(genreId);
    setPickerOpen(false);
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {genres.map((genre) => (
          <span
            key={genre.arrangementGenreId}
            className="inline-flex items-center gap-1 rounded-full bg-[var(--bg-soft)] px-2.5 py-0.5 text-xs font-medium"
          >
            {genre.genreName}
            <button
              type="button"
              onClick={() => onRemove(genre.genreId)}
              aria-label={`Remove ${genre.genreName}`}
              className="rounded-full p-0.5 hover:bg-destructive/10 hover:text-destructive"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <button
          type="button"
          onClick={() => setPickerOpen(!pickerOpen)}
          aria-label="Add genre"
          className="inline-flex items-center gap-1 rounded-full border border-dashed border-[var(--rule)] px-2.5 py-0.5 text-xs font-medium text-[var(--ink-3)] hover:bg-[var(--bg-soft)]"
        >
          <Plus className="h-3 w-3" />
          Add genre
        </button>
      </div>

      {pickerOpen && unassignedGenres.length > 0 && (
        <div className="flex flex-wrap gap-2 rounded-lg border bg-[var(--bg-surface)] p-3">
          {unassignedGenres.map((genre) => (
            <button
              key={genre.genreId}
              type="button"
              onClick={() => handleSelect(genre.genreId)}
              className="rounded-full bg-[var(--bg-soft)] px-2.5 py-0.5 text-xs font-medium hover:bg-[var(--bg-soft)]"
            >
              {genre.prettyName}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
